export type AppVersionCheckerDeps = {
  /** The version this bundle was built with (injected at build time). */
  currentVersion: string;
  /** Resolves the version currently deployed on the server, null if unknown. */
  fetchVersion: () => Promise<string | null>;
  onNewVersion: (serverVersion: string) => void;
};

/**
 * Compares the running bundle's version against the deployed one and fires
 * onNewVersion exactly once per distinct server version. Fetch failures are
 * swallowed — a missed check is harmless, the next one will catch up.
 */
export function createAppVersionChecker({
  currentVersion,
  fetchVersion,
  onNewVersion,
}: AppVersionCheckerDeps) {
  let inFlight: Promise<void> | null = null;
  let lastNotifiedVersion: string | null = null;

  const runCheck = async () => {
    let serverVersion: string | null = null;
    try {
      serverVersion = await fetchVersion();
    } catch {
      return;
    }
    if (!serverVersion) return;
    if (serverVersion === currentVersion) return;
    if (serverVersion === lastNotifiedVersion) return;
    lastNotifiedVersion = serverVersion;
    onNewVersion(serverVersion);
  };

  return {
    check() {
      if (!inFlight) {
        inFlight = runCheck().finally(() => {
          inFlight = null;
        });
      }
      return inFlight;
    },
  };
}
